/**
 * Type definitions for Control Graph Editor
 *
 * These types mirror the Python msgspec definitions in:
 * - cortex/data_types/control_nodes.py
 * - cortex/data_types/control_nodes_questions.py
 * - cortex/data_types/control_graph.py
 */

// ============================================================================
// Enums
// ============================================================================

export enum VariableReferenceType {
  RVAL = "rval",
  LVAL = "lval",
  CONSTANT = "constant",
}

export enum ValueType {
  INT = "int",
  FLOAT = "float",
  STR = "str",
  BOOL = "bool",
  SET_STR = "set[str]",
}

export enum TransformKind {
  IDENTITY = "identity",
  TO_INT = "to_int",
  TO_FLOAT = "to_float",
  TO_BOOL = "to_bool",
  TO_STR = "to_str",
  EXPR = "expr",
}

export enum NodeType {
  DECLARE = "declare",
  SEQUENCE = "sequence",
  MAP = "map",
  GROUP = "group",
  IF = "if",
  ANALYZE = "analyze",
  SHARE_INFO = "share_info",
  INFLUENCE = "influence",
  ASSIGN_ACTION = "assign_action",
  APPEND_ACTION = "append_action",
  QUESTION = "question",
}

export enum QuestionAnswerType {
  BOOLEAN = "boolean",
  STRING = "string",
  INTEGER = "integer",
  FLOAT = "float",
}

// ============================================================================
// Common Structures
// ============================================================================

export interface VariablePath {
  parts: string[];
}

export interface VariableReference {
  node_path: VariablePath | null;
  name: string | null;
  constant: any;
  vtype: VariableReferenceType;
}

export interface DeclareVar {
  name: string;
  node_tag: "declare_var";
  vtype: ValueType;
  default: any;
}

// Criteria type for AnalyzeNode (from assessment_types.py)
export interface Criteria {
  id: string;
  analyze_question: string;
  guidance?: string | null;
  complete_required?: boolean;
  answer_type?: string;
  answer_guidance?: string | null;
  consider_asked_when_primary_is_asked?: boolean;
}

// GoalQuestion type for QuestionNode (simplified from assessment_types.py)
export interface GoalQuestion {
  id: string;
  question_type: string;
  required: boolean;
  initial_question: string;
  topic: string;
  criteria: Criteria[];
  min_num_turns: number;
  max_num_turns: number;
  min_criteria_met: number;
}

// ============================================================================
// Base Node
// ============================================================================

export interface BaseControlNode {
  id: string;
  node_tag: string;
  parent: string | null;
  children: string[];
  priority: number;
  is_exclusive_open: boolean;
}

// ============================================================================
// Control Flow Nodes
// ============================================================================

export interface DeclareNode extends BaseControlNode {
  node_tag: "declare";
  variables: DeclareVar[];
}

export interface SequenceNode extends BaseControlNode {
  node_tag: "sequence";
}

export interface MapNode extends BaseControlNode {
  node_tag: "map";
  source_set_var: string;
  iter_var: string;
}

export interface GroupNode extends BaseControlNode {
  node_tag: "group";
}

export interface IfNode extends BaseControlNode {
  node_tag: "if";
  expr: string;
}

// ============================================================================
// Action Nodes
// ============================================================================

export interface AnalyzeNode extends BaseControlNode {
  node_tag: "analyze";
  criteria: Criteria;
  close_on_finalize: boolean;
}

export interface ShareInfoNode extends BaseControlNode {
  node_tag: "share_info";
  template: string;
  is_exclusive_open: true;
}

export interface InfluenceNode extends BaseControlNode {
  node_tag: "influence";
  guidance: string | null;
  valence_delta: number | null;
  activation_delta: number | null;
  engagement_delta: number | null;
  ends_scenario: boolean;
  exit_type: string;
  is_exclusive_open: true;
}

export interface AssignAction extends BaseControlNode {
  node_tag: "assign_action";
  target: VariableReference;
  source: VariableReference;
  value_template: string | null;
  transform: TransformKind;
  transform_expr: string | null;
}

export interface AppendAction extends BaseControlNode {
  node_tag: "append_action";
  target: VariableReference;
  source: VariableReference;
  value_template: string | null;
  transform: TransformKind;
  transform_expr: string | null;
}

export interface QuestionNode extends BaseControlNode {
  node_tag: "question";
  goal_question: GoalQuestion | null;
  question_id: string | null;
  is_exclusive_open: true;
}

// ============================================================================
// Union Type
// ============================================================================

export type ControlNode =
  | DeclareNode
  | SequenceNode
  | MapNode
  | GroupNode
  | IfNode
  | AnalyzeNode
  | ShareInfoNode
  | InfluenceNode
  | AssignAction
  | AppendAction
  | QuestionNode;

// ============================================================================
// Control Graph
// ============================================================================

export interface ControlGraph {
  nodes: Record<string, ControlNode>;
  root: string;
}

// ============================================================================
// Node Metadata for UI
// ============================================================================

export interface NodeMetadata {
  type: NodeType;
  label: string;
  description: string;
  icon: string;
  color: string;
  category: "control" | "action";
  isExclusive: boolean;
}

export const NODE_METADATA: Record<NodeType, NodeMetadata> = {
  [NodeType.DECLARE]: {
    type: NodeType.DECLARE,
    label: "Declare",
    description: "Declare variables that child nodes can use throughout the conversation",
    icon: "📝",
    color: "#9333EA",
    category: "control",
    isExclusive: false,
  },
  [NodeType.SEQUENCE]: {
    type: NodeType.SEQUENCE,
    label: "Sequence",
    description: "Execute children one after another in order - useful for multi-step flows",
    icon: "➡️",
    color: "#3B82F6",
    category: "control",
    isExclusive: false,
  },
  [NodeType.MAP]: {
    type: NodeType.MAP,
    label: "Map",
    description: "Loop through a collection, repeating children for each item",
    icon: "🔄",
    color: "#10B981",
    category: "control",
    isExclusive: false,
  },
  [NodeType.GROUP]: {
    type: NodeType.GROUP,
    label: "Group",
    description: "Group nodes together - all children can run in parallel",
    icon: "📦",
    color: "#6B7280",
    category: "control",
    isExclusive: false,
  },
  [NodeType.IF]: {
    type: NodeType.IF,
    label: "If",
    description: "Conditional branch - only execute children if condition is true",
    icon: "❓",
    color: "#F59E0B",
    category: "control",
    isExclusive: false,
  },
  [NodeType.ANALYZE]: {
    type: NodeType.ANALYZE,
    label: "Analyze",
    description: "Analyze user responses against specific criteria to extract insights",
    icon: "🔍",
    color: "#8B5CF6",
    category: "action",
    isExclusive: false,
  },
  [NodeType.SHARE_INFO]: {
    type: NodeType.SHARE_INFO,
    label: "Share Info",
    description: "Bot shares information by speaking a message - use templates with {{variables}}",
    icon: "💬",
    color: "#06B6D4",
    category: "action",
    isExclusive: true,
  },
  [NodeType.INFLUENCE]: {
    type: NodeType.INFLUENCE,
    label: "Influence",
    description: "Control bot's emotional state and behavior - can adjust mood and end scenarios",
    icon: "🎭",
    color: "#EC4899",
    category: "action",
    isExclusive: true,
  },
  [NodeType.ASSIGN_ACTION]: {
    type: NodeType.ASSIGN_ACTION,
    label: "Assign",
    description: "Set a variable to a specific value - useful for storing analysis results",
    icon: "=",
    color: "#14B8A6",
    category: "action",
    isExclusive: false,
  },
  [NodeType.APPEND_ACTION]: {
    type: NodeType.APPEND_ACTION,
    label: "Append",
    description: "Add an item to a collection (set) - builds up lists over time",
    icon: "+",
    color: "#84CC16",
    category: "action",
    isExclusive: false,
  },
  [NodeType.QUESTION]: {
    type: NodeType.QUESTION,
    label: "Question",
    description: "Bot asks an interview question and waits for user response",
    icon: "❔",
    color: "#F43F5E",
    category: "action",
    isExclusive: true,
  },
};
